import type { Translations } from './types';

const en: Translations = {
  locale: 'en-US',

  shared: {
    continue: 'Continue',
    otherNotSure: "Other/I'm not sure",
  },

  gold: {
    needHelp: 'Need some help?',
    karatHelp: 'The karat value is usually stamped on your jewelry as 10K, 14K, 18K, or 24K.',
    karatRingsBold: 'rings',
    karatRingsRest: ', check the inside of the band.',
    karatNecklacesBold: 'necklaces or bracelets',
    karatNecklacesRest: ', look near the clasp.',
    karatEarringsBold: 'earrings',
    karatEarringsRest: ', it may be on the post or back.',
    purityHelp: 'The purity value is usually stamped on your piece. Look for stamps such as:',
    alsoKnownAs: ' also known as ',
    or: ' or ',
    additionalComments: 'Additional comments (optional)',
  },

  itemCard: {
    estimatedValue: 'Estimated value',
    internalStorage: 'Internal Storage',
    purity: 'Purity',
    condition: 'Condition',
    remove: 'Remove',
    edit: 'Edit',
    storeCardOtherMessage: (attrs) =>
      `We need the ${attrs} of your item to provide you with an estimated value.\n\nYou can also bring your item to one of our stores for an in-person review.`,
    storeCardDefaultMessage: "You'll need to bring this item to one of our stores for an in-person review.",
  },

  otp: {
    almostThere: 'Almost there!',
    quotesDeliveredByText:
      'Quotes are delivered to you by text. To receive your quote, please provide a phone number below.',
    customerName: 'Name',
    phoneNumber: 'Phone Number',
    phonePlaceholder: '123 202 2020',
    phoneInfo: "We'll text your quote estimate and reference number.",
    zipCode: 'ZIP Code',
    zipPlaceholder: '78109',
    zipTooltip: 'Your zip code helps us suggest local stores.',
    phoneError: 'Please enter a valid 10-digit phone number.',
    zipError5Digit: 'Please enter a valid 5-digit US ZIP code.',
    zipErrorInvalid: 'Please enter a valid zip code.',
    consentLabel: 'Yes, Please Text a Copy of My Quote and Additional Offers From Ezpawn!',
    consentPrefix: (brandA) =>
      `By checking this box, I agree to receive one or more text messages related to my quote from ${brandA} and their family of stores at the phone number provided. I also agree to receive recurring automated promotional text messages, including special offers, promotions, and updates, from `,
    consentEzPlusStoresLink: 'EZ+ & Stores',
    consentMiddle:
      ' at the phone number provided. I authorize the use of an automated system to send these messages. Consent is not a condition of any purchase or service. Text HELP to 81910 for help or STOP to cancel texts. Message frequency varies. Message and data rates may apply. See ',
    privacyPolicyLink: 'Privacy Policy',
    consentAnd: 'and',
    termsLink: 'Terms & Conditions',
    consentPost: '.',
    sending: 'Sending...',
    verifyTitle: 'Verify your phone number',
    enterCode: 'Please enter the code sent to',
    didntGetCode: "Didn't get a code?",
    resending: 'Resending...',
    resendCode: 'Resend code',
    phoneVerified: 'Phone Number Verified',
    emailTitle: 'We need your email',
    emailDescription: "We'll send you a copy of this quote so you can reference it later.",
    emailLabel: 'Email Address',
    emailPlaceholder: 'your@email.com',
    optInPromotions: 'Opt into Email Promotions',
    promotionsTextPrefix: 'By clicking the box, you agree to receive personalized promotional email messages from ',
    promotionsTextLink: 'EZCORP® subsidiaries & affiliates',
    promotionsTextSuffix:
      ' at the email address provided. Consent is not a condition of any purchase or required to access the site. You can unsubscribe from promotional emails at anytime using the link at the bottom of the email. See ',
    invalidEmail: 'Please enter a valid email address.',
    failedEmail: 'Failed to send email. Please try again.',
    somethingWrong: 'Something went wrong. Please try again.',
    verifying: 'Verifying...',
    failedOtp: 'Failed to send OTP. Please try again.',
    invalidOtp: 'Invalid OTP. Please try again.',
    emailSent: 'Email Sent!',
    send: 'Send',
    tncPre: 'For more information see our',
    termsOfUse: 'Terms of use',
    tncMid: 'and',
    privacyPolicy: 'Privacy policy.',
  },

  quoteModal: {
    title: 'What impacts the value offered?',
    intro:
      'We strive to offer fair prices for your items. However, there are a few key reasons why we offer less cash than what the product might be worth in a retail setting:',
    resaleRisk:
      "Resale Risk: When we buy an item, there's a risk that it may take time to resell, or it might not sell at all. The offer we make accounts for this uncertainty.",
    operatingCosts:
      'Operating Costs: Like any business, we have overhead costs, such as staff, store operations, and security. This influences the price we can pay while staying in business.',
    marketDemand:
      'Market Demand: The resale value of items depends heavily on market trends and demand. Some items may hold their value well, while others depreciate over time. We base our offers on current market demand.',
  },

  introModal: {
    title: 'Instant Quote Tool',
    describeItem: 'Describe your item',
    describeItemDesc: "We'll guide you through the details.",
    provideContact: 'Provide your phone number and ZIP code',
    provideContactDesc: 'Needed to generate the quote.',
    getQuoteInstantly: 'Get your quote instantly',
    getQuoteInstantlyDesc: 'No need to drive and wait in lines.',
    note: 'You can add up to 3 items per quote.',
    getStarted: 'Get Started',
  },

  category: {
    itemType: 'Item type',
    selectOption: 'Select an option',
    weight: 'weight',
    karat: 'karat',
    purity: 'purity',
    goldInfoPre: 'Your estimate will be based on',
    goldInfoAnd: 'and',
    goldInfoPost: 'for now. We can confirm the rest in person.',
    cantFindNote: "Can't find what you're looking for? We'll be adding more categories in the future!",
    visitStore: 'Or visit a store for an in-person quote',
  },

  attributes: {
    searchPlaceholder: (attrName) => `Search for a ${attrName}`,
    warningMessage: "We won't be able to provide you an accurate estimate without this information.",
    errorMustSelect: (attrName) => `You must select a ${attrName} to continue`,
    displayName: (name) => (name === 'Quality' ? 'Purity' : name),
  },

  finalReview: {
    totalEstimate: 'Total estimated quote',
    quoteId: (id) => `Quote ID: ${id}`,
    howDerived: 'How was this quote derived?',
    expertReviewNeeded: 'In-store review needed for some of your items.',
    expertReviewGeneralMessage: (categoryNames, itemCount) =>
      itemCount > 1
        ? `We can price these once we've seen the items. We couldn't price your ${categoryNames} online, but if they're in good condition we'll make you an offer. Come visit us and one of our team members would be happy to take a closer look.`
        : `We can price this once we've seen it. We couldn't price your ${categoryNames} online, but if it's in good condition we'll make you an offer. Come visit us and one of our team members would be happy to take a closer look.`,
    expertReviewMultipleMessage:
      "We can price these once we've seen the items. We couldn't price some of the items online, but if they're in good condition we'll make you an offer. Come visit us and one of our team members would be happy to take a closer look.",
    expertReviewJewelryMessage:
      "We can price this once we've seen it. Jewelry comes down to weight, karat and purity and we didn't have what's necessary for this piece. Come visit us and one of our team members would be happy to test it with you, then make you an offer.",
    diamondNote: (metalLabel) =>
      `This estimate is based on the resale ${metalLabel} value only. Diamonds, precious stones or other materials are not included in the estimate and require in-store verification.`,
    conditionNote:
      'Item condition, including whether the item is broken, may affect the final value assessed in store.',
    whyInstore: 'Why in-store review?',
    modelsMissing: 'Details like model numbers might be missing.',
    authenticity: 'Some items may need to be verified for authenticity.',
    premiumMaterials: 'We might need to check for premium materials.',
    emailCopy: 'Email me a copy',
    emailLimit: "You've already emailed this quote the maximum number of times.",
    quoteDetailsBtn: 'Quote details',
    bringItems: 'Bring your items to one of our stores for a detailed review:',
    storesNear: (zipCode) => `Stores near ${zipCode}`,
    storeClosestToYou: 'Store closest to you',
    getDirections: 'Get directions',
    findAnotherStore: 'Find another store',
    quoteDetailsHeading: 'Quote Details',
    startNewQuote: 'Start a new quote',
  },

  summary: {
    addAnotherItem: 'Add Another Item',
    maxItems: "You've reached the maximum number of items for this quote.",
    canAddPre: 'You can add',
    canAddMid: (remaining) => (remaining === 1 ? 'more item' : 'more items'),
    canAddPost: 'to this quote.',
    getMyQuote: 'Get My Quote',
  },

  ezquote: {
    sessionExpired: "Your session expired due to inactivity\nLet's get you a fresh quote!",
    bestCondition: 'Best condition',
    bestConditionDesc: 'Like new, minimal signs of use',
    goodCondition: 'Good condition',
    goodConditionDesc: 'Gently used, minor visible wear',
    fairCondition: 'Fair condition',
    fairConditionDesc: 'Noticeable wear but fully functional',
    errorSelectCategory: 'Please select a category.',
    errorSelectCondition: 'Please select a condition.',
    invalidStep: 'Invalid Step',
  },

  savedQuote: {
    notFoundMessage: "We couldn't find a quote with that ID. It may have expired or the link may be incorrect.",
  },

  headers: {
    step2Title: 'Select your item category',
    step2Subtitle: 'Choose the category that best fits your item.',
    step3Title: "Let's narrow it down",
    step3Subtitle: 'Please provide the following details for your item.',
    step4Title: "What's the condition of your item?",
    step4Subtitle: 'Choose the condition that best describes your item.',
    step5Title: "Here's a summary of your items",
    step5Subtitle: "We'll estimate their value on the next step.",
    step6Title: "Here's your estimated quote",
    step6Subtitle: 'We encourage you to hurry into the store as values may change over time.',
    goldNarrowTitle: "Let's narrow it down",
    goldNarrowSubtitle: 'Please select what type of item you have.',
    goldWeightTitle: "What's the estimated weight of your item?",
    goldWeightSubtitle:
      'A rough estimate works fine. The closer your estimate, the more accurate your quote will be.',
    goldKaratTitle: 'Do you know the karats?',
    goldKaratSubtitle: "Please select the gold's karat value.",
    goldPurityTitle: 'Do you know the purity?',
    goldPuritySubtitle: 'Please select the purity value.',
    goldDetailsTitle: "Any other details you'd like us to consider?",
    goldDetailsSubtitle: 'You can include information like the brand, one of a kind details and more.',
    goldDiamondsTitle: 'Does your item have diamonds or precious stones?',
    goldDiamondsSubtitle: 'Choose the option that best describes your item.',
  },
};

export default en;
